import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { documentService } from '../services/documentService';
import { Document } from '../types/Document';

const DocumentViewerPage: React.FC = () => {
  const { folderId, documentId } = useParams<{ folderId: string; documentId: string }>();
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!folderId || !documentId) {
      setError('Missing folder ID or document ID');
      setLoading(false);
      return;
    }

    const loadDocument = async () => {
      try {
        setLoading(true);
        const doc = await documentService.getDocumentByFolderAndId(folderId, documentId);
        
        if (doc) {
          setDocument(doc);
        } else {
          setError('Document not found');
        }
      } catch (err) {
        setError('Failed to load document');
        console.error('Error loading document:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [folderId, documentId]);

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ 
            textAlign: 'center', 
            padding: 'var(--space-2xl)',
            color: 'rgba(26, 31, 58, 0.6)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: 'var(--space-lg)' }}>📄</div>
            <h2>Loading Document {documentId}</h2>
            <p>Please wait while we load the document and extract text content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ 
            textAlign: 'center', 
            padding: 'var(--space-2xl)',
            color: 'rgba(26, 31, 58, 0.6)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: 'var(--space-lg)' }}>❌</div>
            <h2>Error Loading Document</h2>
            <p>{error || 'Document not found'}</p>
            <div style={{ marginTop: 'var(--space-lg)' }}>
              <Link to="/documents" className="btn">
                ← Back to Documents
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card patriotic-accent">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 'var(--space-md)',
          marginBottom: 'var(--space-lg)'
        }}>
          <div>
            <h1>{document.filename}</h1>
            <div style={{
              display: 'flex',
              gap: 'var(--space-md)',
              alignItems: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'rgba(26, 31, 58, 0.7)',
              marginTop: 'var(--space-sm)'
            }}>
              <span>📁 Folder {document.folder}</span>
              <span>•</span>
              <span>🆔 Document ID: {document.id}</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <Link to="/documents" className="btn btn-secondary">
              ← Documents
            </Link>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Document Image</h2>
        <div style={{
          position: 'relative',
          backgroundColor: 'var(--democracy-silver)',
          borderRadius: '16px',
          overflow: 'hidden',
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {!imageError ? (
            <>
              <img
                src={document.imagePath}
                alt={`Document ${document.filename}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}
              />
              {!imageLoaded && (
                <div style={{
                  position: 'absolute',
                  color: 'rgba(26, 31, 58, 0.5)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Loading image...
                </div>
              )}
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              color: 'rgba(26, 31, 58, 0.6)',
              padding: 'var(--space-xl)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: 'var(--space-md)' }}>📄</div>
              <h3>Image Not Available</h3>
              <p>The document image could not be loaded.</p>
              <a
                href={document.imagePath}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ marginTop: 'var(--space-md)' }}
              >
                Try Direct Link
              </a>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          gap: 'var(--space-md)',
          marginTop: 'var(--space-md)',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          <a
            href={document.imagePath}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            🖼️ Open Image in New Tab
          </a>
          <a
            href={document.textPath}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            📄 Download Text File
          </a>
        </div>
      </div>

      <div className="card">
        <h2>Extracted Text Content</h2>
        <p style={{ 
          color: 'rgba(26, 31, 58, 0.7)',
          fontSize: 'var(--font-size-sm)',
          marginBottom: 'var(--space-lg)'
        }}>
          🔍 Processed with Tesseract OCR • Text is searchable via Google Custom Search
        </p>
        
        <div style={{
          backgroundColor: 'var(--democracy-silver)',
          border: '1px solid rgba(0, 40, 104, 0.1)',
          borderRadius: '16px',
          padding: 'var(--space-lg)',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 'var(--font-size-sm)',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
          maxHeight: '600px',
          overflow: 'auto',
          color: 'var(--democracy-navy)'
        }}>
          {document.extractedText || 'Text content not available or still processing...'}
        </div>

        {document.extractedText && (
          <div style={{
            marginTop: 'var(--space-md)',
            padding: 'var(--space-md)',
            backgroundColor: 'var(--democracy-light-blue)',
            borderRadius: '12px',
            fontSize: 'var(--font-size-sm)'
          }}>
            <strong style={{ color: 'var(--democracy-blue)' }}>📊 Text Statistics:</strong>
            <div style={{ marginTop: 'var(--space-xs)', color: 'rgba(26, 31, 58, 0.8)' }}>
              • {document.extractedText.length.toLocaleString()} characters
              • {document.extractedText.split(/\s+/).length.toLocaleString()} words
              • {document.extractedText.split(/\n/).length.toLocaleString()} lines
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Document Actions</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--space-md)'
        }}>
          <button 
            onClick={() => navigator.clipboard?.writeText(document.extractedText || '')}
            className="btn btn-secondary"
          >
            📋 Copy Text Content
          </button>
          <button 
            onClick={() => navigator.share?.({ 
              title: document.filename, 
              url: window.location.href 
            })}
            className="btn btn-secondary"
          >
            🔗 Share Document
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerPage;